import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import { getShiftTimes, getHubCleaning, saveHubCleaning } from '../services/firebase';
import './Admin.css';

const DAYS = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];
const DAY_LABELS = {
  'Thu-April-9':  'Thursday, April 9',
  'Fri-April-10': 'Friday, April 10',
  'Sat-April-11': 'Saturday, April 11',
  'Sun-April-12': 'Sunday, April 12',
};
const AREAS = ['Bathrooms', 'Lobby', 'Auditorium'];
const BLANK = { areas: { Bathrooms: [], Lobby: [], Auditorium: [] } };

function HubCleaning({ onLogout }) {
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [draft, setDraft] = useState(BLANK);   // working copy
  const [saved, setSaved] = useState(BLANK);   // last committed state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const loadShiftTimes = useCallback(async () => {
    const times = await getShiftTimes();
    setShiftTimes(times.filter(s => s.active));
  }, []);

  const loadDay = useCallback(async (day) => {
    setLoading(true);
    const doc = await getHubCleaning(day);
    const data = doc || { ...BLANK };
    setDraft(data);
    setSaved(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadShiftTimes(); }, [loadShiftTimes]);
  useEffect(() => { loadDay(selectedDay); }, [selectedDay, loadDay]);

  const handleDayChange = async (day) => {
    if (day === selectedDay) return;
    // Auto-save pending changes before switching days
    if (isDirty) {
      setSaving(true);
      await saveHubCleaning(selectedDay, draft);
      setSaving(false);
    }
    setSelectedDay(day);
  };

  const handleSave = async () => {
    setSaving(true);
    await saveHubCleaning(selectedDay, draft);
    setSaved(draft);
    setSaving(false);
  };

  const handleCancel = () => {
    setDraft(saved);
  };

  const getAreaRows = (area) => (draft?.areas?.[area] || []);

  const addRow = (area) => {
    const areas = { ...(draft?.areas || {}) };
    areas[area] = [...(areas[area] || []), { name: '', shiftTime: '', note: '' }];
    setDraft({ ...draft, areas });
  };

  const updateRow = (area, idx, field, value) => {
    const areas = { ...(draft?.areas || {}) };
    const rows = [...(areas[area] || [])];
    rows[idx] = { ...rows[idx], [field]: value };
    areas[area] = rows;
    setDraft({ ...draft, areas });
  };

  const removeRow = (area, idx) => {
    const areas = { ...(draft?.areas || {}) };
    areas[area] = (areas[area] || []).filter((_, i) => i !== idx);
    setDraft({ ...draft, areas });
  };

  if (loading) return <div className="admin-container"><AdminNav onLogout={onLogout} /><div className="loading">Loading…</div></div>;

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Cleaning Schedule</h1>
        <p className="page-subtitle">Schedule volunteers to keep the Kingdom Hall clean throughout the event day.</p>
      </header>

      <div className="scheduling-content">
        {/* Day Tabs */}
        <div className="day-selector">
          {DAYS.map(day => (
            <button
              key={day}
              className={`day-tab ${selectedDay === day ? 'active' : ''}`}
              onClick={() => handleDayChange(day)}
            >
              {day.split('-')[0]}
            </button>
          ))}
        </div>

        {/* Save / Cancel bar */}
        {isDirty && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '16px 0', padding: '12px 16px', background: '#fefce8', border: '1px solid #fde047', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', color: '#92400e', flex: 1 }}>You have unsaved changes.</span>
            <button className="btn btn-primary" style={{ flex: 'none', minWidth: 'auto', padding: '6px 16px', fontSize: '13px' }} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-secondary" style={{ flex: 'none', minWidth: 'auto', padding: '6px 16px', fontSize: '13px' }} onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        )}

        <h3 style={{ marginTop: isDirty ? '8px' : '24px', marginBottom: '16px', color: '#1e293b' }}>
          {DAY_LABELS[selectedDay]}
        </h3>

        {AREAS.map(area => (
          <div key={area} className="location-section" style={{ marginBottom: '20px' }}>
            <div className="shift-header" style={{ padding: '14px 20px', background: '#f8fafc', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>{area}</strong>
              <button
                className="btn btn-primary"
                style={{ flex: 'none', minWidth: 'auto', padding: '4px 12px', fontSize: '12px' }}
                onClick={() => addRow(area)}
              >
                + Add Volunteer
              </button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              {getAreaRows(area).length === 0 && (
                <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>No volunteers scheduled. Click "+ Add Volunteer".</p>
              )}
              {getAreaRows(area).length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr auto', gap: '8px', marginBottom: '6px' }}>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: '#64748b' }}>Name</div>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: '#64748b' }}>Shift Time</div>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: '#64748b' }}>Note</div>
                  <div />
                </div>
              )}
              {getAreaRows(area).map((row, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr auto', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <input
                    className="search-input"
                    style={{ padding: '7px 10px' }}
                    placeholder="Name"
                    value={row.name || ''}
                    onChange={e => updateRow(area, idx, 'name', e.target.value)}
                  />
                  <select
                    className="search-input"
                    style={{ padding: '7px 10px' }}
                    value={row.shiftTime || ''}
                    onChange={e => updateRow(area, idx, 'shiftTime', e.target.value)}
                  >
                    <option value="">Any time</option>
                    {shiftTimes.map(st => <option key={st.name} value={st.name}>{st.name}</option>)}
                  </select>
                  <input
                    className="search-input"
                    style={{ padding: '7px 10px' }}
                    placeholder="Optional note"
                    value={row.note || ''}
                    onChange={e => updateRow(area, idx, 'note', e.target.value)}
                  />
                  <button className="btn-delete" onClick={() => removeRow(area, idx)} title="Remove">×</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default HubCleaning;
